import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  getMyPanels,
  getPanelById,
  getPanelByOpportunityId,
  joinPanel,
  getDiscussions,
  createDiscussion,
  getDiscussionById,
  editDiscussion,
  deleteDiscussion,
  toggleLike,
  togglePin,
  flagDiscussion,
  addComment,
  getComments,
  editComment,
  deleteComment,
  getHotDiscussions,
  getTrendingTopics,
  getPanelMembers,
} from '../api/panels-api';

// ── Panel hooks ───────────────────────────────────────────────────────────────
export const useMyPanels = () => {
  return useQuery({
    queryKey: ['my-panels'],
    queryFn: getMyPanels,
  });
};

export const usePanelById = (panelId) => {
  return useQuery({
    queryKey: ['panel', panelId],
    queryFn: () => getPanelById(panelId),
    enabled: !!panelId,
  });
};

export const usePanelByOpportunityId = (opportunityId) => {
  return useQuery({
    queryKey: ['panel-by-opportunity', opportunityId],
    queryFn: () => getPanelByOpportunityId(opportunityId),
    enabled: !!opportunityId,
  });
};

export const useJoinPanel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: joinPanel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-panels'] });
    },
  });
};

// ── Discussion hooks ──────────────────────────────────────────────────────────
export const useDiscussions = (panelId, sort = 'recent', tag = null, q = null) => {
  return useInfiniteQuery({
    queryKey: ['discussions', panelId, sort, tag, q],
    queryFn: ({ pageParam = 1 }) => getDiscussions({ panelId, sort, page: pageParam, limit: 3, tag, q }),
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.meta) return undefined;
      const { page, totalPages } = lastPage.meta;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!panelId,
  });
};

export const useCreateDiscussion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDiscussion,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['discussions', variables.panelId] });
      queryClient.invalidateQueries({ queryKey: ['panel', variables.panelId] });
      queryClient.invalidateQueries({ queryKey: ['hot-discussions', variables.panelId] });
      queryClient.invalidateQueries({ queryKey: ['trending-topics', variables.panelId] });
    },
  });
};

export const useDiscussionById = (panelId, discussionId) => {
  return useQuery({
    queryKey: ['discussion', discussionId],
    queryFn: () => getDiscussionById({ panelId, discussionId }),
    enabled: !!panelId && !!discussionId,
  });
};

export const useToggleLike = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleLike,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['discussions', variables.panelId] });
    },
  });
};

export const useTogglePin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: togglePin,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['discussions', variables.panelId] });
      queryClient.invalidateQueries({ queryKey: ['hot-discussions', variables.panelId] });
    },
  });
};

export const useFlagDiscussion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: flagDiscussion,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['discussions', variables.panelId] });
    },
  });
};

export const useAddComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addComment,
    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['comments', variables.discussionId] });
      const previousComments = queryClient.getQueryData(['comments', variables.discussionId]);

      // Optimistically prepend the new comment to the first page
      if (!variables.parentId) {
        queryClient.setQueryData(['comments', variables.discussionId], (old) => {
          if (!old) return old;
          const optimisticComment = {
            id: `optimistic-${Date.now()}`,
            content: variables.content,
            authorId: 'me',
            discussionId: variables.discussionId,
            parentId: null,
            isDeleted: false,
            replies: [],
            _count: { replies: 0 },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            author: { name: 'You' },
          };
          const newPages = old.pages.map((page, i) =>
            i === 0 ? { ...page, data: [optimisticComment, ...(page.data || [])] } : page
          );
          return { ...old, pages: newPages };
        });
      }
      return { previousComments };
    },
    onError: (_err, variables, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(['comments', variables.discussionId], context.previousComments);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ['comments', variables.discussionId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', variables.panelId] });
    },
  });
};

export const useComments = (panelId, discussionId) => {
  return useInfiniteQuery({
    queryKey: ['comments', discussionId],
    queryFn: ({ pageParam = 1 }) => getComments({ panelId, discussionId, page: pageParam, limit: 5 }),
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.meta) return undefined;
      const { page, totalPages } = lastPage.meta;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!panelId && !!discussionId,
  });
};

export const useEditComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editComment,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['comments', variables.discussionId] });
      const previousComments = queryClient.getQueryData(['comments', variables.discussionId]);

      // Optimistically update the comment content in-place across all pages
      queryClient.setQueryData(['comments', variables.discussionId], (old) => {
        if (!old) return old;
        const newPages = old.pages.map((page) => ({
          ...page,
          data: page.data.map((c) => {
            if (c.id === variables.commentId) {
              return { ...c, content: variables.content, updatedAt: new Date().toISOString() };
            }
            // Also check replies
            return {
              ...c,
              replies: c.replies.map((r) =>
                r.id === variables.commentId
                  ? { ...r, content: variables.content, updatedAt: new Date().toISOString() }
                  : r
              ),
            };
          }),
        }));
        return { ...old, pages: newPages };
      });

      return { previousComments };
    },
    onError: (_err, variables, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(['comments', variables.discussionId], context.previousComments);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ['comments', variables.discussionId] });
    },
  });
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteComment,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['comments', variables.discussionId] });
      const previousComments = queryClient.getQueryData(['comments', variables.discussionId]);

      // Optimistically tombstone or remove the comment
      queryClient.setQueryData(['comments', variables.discussionId], (old) => {
        if (!old) return old;
        const newPages = old.pages.map((page) => ({
          ...page,
          data: page.data
            .map((c) => {
              if (c.id === variables.commentId) {
                // If it has replies, show tombstone; otherwise remove
                return c.replies?.length > 0
                  ? { ...c, isDeleted: true, content: '' }
                  : null;
              }
              // Also filter replies
              return {
                ...c,
                replies: c.replies.filter((r) => r.id !== variables.commentId),
              };
            })
            .filter(Boolean),
        }));
        return { ...old, pages: newPages };
      });

      return { previousComments };
    },
    onError: (_err, variables, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(['comments', variables.discussionId], context.previousComments);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ['comments', variables.discussionId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', variables.panelId] });
    },
  });
};

// ── Sidebar hooks ─────────────────────────────────────────────────────────────
export const useHotDiscussions = (panelId) => {
  return useQuery({
    queryKey: ['hot-discussions', panelId],
    queryFn: () => getHotDiscussions(panelId),
    enabled: !!panelId,
  });
};

export const useTrendingTopics = (panelId) => {
  return useQuery({
    queryKey: ['trending-topics', panelId],
    queryFn: () => getTrendingTopics(panelId),
    enabled: !!panelId,
  });
};

export const useEditDiscussion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editDiscussion,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['discussions', variables.panelId] });
      queryClient.invalidateQueries({ queryKey: ['discussion', variables.discussionId] });
      queryClient.invalidateQueries({ queryKey: ['hot-discussions', variables.panelId] });
      queryClient.invalidateQueries({ queryKey: ['trending-topics', variables.panelId] });
    },
  });
};

export const useDeleteDiscussion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDiscussion,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['discussions', variables.panelId] });
      queryClient.invalidateQueries({ queryKey: ['hot-discussions', variables.panelId] });
      queryClient.invalidateQueries({ queryKey: ['trending-topics', variables.panelId] });
    },
  });
};

export const usePanelMembers = (panelId, search = '') => {
  return useQuery({
    queryKey: ['panel-members', panelId, search],
    queryFn: () => getPanelMembers({ panelId, search }),
    enabled: !!panelId,
  });
};
